import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type { CurrentUser, Unread } from "~/slices/users/types";
import { useCurrentUserStore } from "~/stores/userStore";
import { useUnreadStore } from "~/stores/unreadStore";

type CurrentUserResult = {
  data: Ref<CurrentUser>;
  isLoading: Ref<boolean>;
  isError: Ref<boolean>;
  error: Ref<unknown>;
  suspense: () => Promise<void>;
};

export const useCurrentUser = (): CurrentUserResult => {
  const config = useRuntimeConfig();
  const { authFetch } = useAuthFetch();

  const { isLoading, suspense, isError, data, error } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () =>
      authFetch<CurrentUser>(`${config.public.baseURL}users/edit_info/`),
  });

  return {
    isLoading,
    suspense,
    data: data as Ref<CurrentUser>,
    isError,
    error,
  };
};

/** After this time the unread query is stale and will refetch (time-based invalidation). */
const UNREAD_INVALIDATE_MS = 60 * 1000; // 1 min

type UnreadResult = {
  data: Ref<Unread | undefined>;
  isLoading: Ref<boolean>;
  isError: Ref<boolean>;
  error: Ref<unknown>;
  suspense: () => Promise<void>;
  /** Manually invalidate and refetch (e.g. after an action that changes unread counts). */
  invalidate: () => Promise<void>;
  /** Call after first paint to start the query (when used with deferred fetch for LCP). */
  runAfterPaint?: () => void;
};

export const useUnread = (): UnreadResult => {
  const config = useRuntimeConfig();
  const queryClient = useQueryClient();
  const unreadStore = useUnreadStore();
  const currentUserStore = useCurrentUserStore();
  const { authFetch } = useAuthFetch();

  const queryEnabled = ref(false);
  const shouldFetchUnread = computed(
    () =>
      queryEnabled.value &&
      currentUserStore.isAuthenticated &&
      currentUserStore.isAdmin,
  );
  const { isLoading, suspense, isError, data, error } = useQuery({
    queryKey: ["unread"],
    queryFn: () =>
      authFetch<Unread>(`${config.public.baseURL}v1/unread/`),
    staleTime: UNREAD_INVALIDATE_MS,
    refetchInterval: UNREAD_INVALIDATE_MS,
    enabled: shouldFetchUnread,
  });

  watch(
    data,
    (v) => {
      unreadStore.setUnread(v ?? null);
    },
    { immediate: true },
  );

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["unread"] });

  const runAfterPaint = () => {
    queryEnabled.value = true;
  };

  return {
    isLoading,
    data: data as Ref<Unread | undefined>,
    suspense,
    isError,
    error,
    invalidate,
    runAfterPaint,
  };
};
